import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';
import { useState } from 'react';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import BulkTempalte941 from '@/components/ui/BulkTempalte941';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form941BulkUpload = () => {
  const [isBulkUploadTemplate, setIsBulkUploadTemplate] = useState(false);
  return (
    <AppLayout
      meta={
        <Meta
          title="Bulk Upload Form 941 online with TaxZerone"
          description="Easily Bulk upload and file Form 941 online with TaxZerone. Upload multiple returns at once using an Excel template, and e-file in just a few clicks."
          keywords=""
          canonical="resources/employment-tax/form-941-bulk-upload/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <BulkTempalte941
        isBulkUploadTemplate={isBulkUploadTemplate}
        setIsBulkUploadTemplate={setIsBulkUploadTemplate}
      />
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          Form 941{' '}
          <span className="highlight-container">
            <span className="highlight">Bulk Upload</span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
            textAlign: 'center',
          }}
        >
          TaxZerone’s Bulk Upload feature helps you to file multiple returns at
          once in just
          <br /> a few easy steps — it’s fast, simple, and saves you time!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941bulk`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-941-bulk-upload/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 941 Bulk Upload at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <b>Form 941 Bulk Upload</b>
                        {
                          ' allows tax professionals, payroll providers, and businesses to prepare and submit '
                        }
                        <b>multiple Form 941 returns at once</b>
                        {
                          " using a single Excel file. Instead of entering each employer's information separately, you can upload your data in bulk, review it, correct errors, and e-file multiple returns efficiently."
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>File Format:</b>
                        {' TaxZerone accepts '}
                        <b>Excel (.xlsx)</b>
                        {
                          ' files for Form 941 bulk uploads. A preformatted template is available to help you enter employer and tax information correctly.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Benefits:</b>
                        {' Bulk upload helps '}
                        <b>{'save time, reduce repetitive data entry, '}</b>
                        <b>identify</b>
                        <b>
                          {
                            ' errors before filing, and simplify multi-client Form 941 e-filing'
                          }
                        </b>
                        {'. Once the returns are '}
                        validated
                        {', multiple returns can be selected and '}
                        submitted
                        {' together.'}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing returns for multiple clients or businesses is easier
                  and more efficient when you have the right tools in place.
                  Bulk upload is a time-saving option for tax professionals
                  handling{' '}
                  <Link
                    href="/resources/employment-tax/form-941-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 941
                  </Link>{' '}
                  filings for multiple businesses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With{' '}
                  <span style={{ fontWeight: 600 }}>
                    TaxZerone’s Bulk Upload Feature
                  </span>
                  , you can quickly import all your Form 941 data at <br /> once
                  — no repetitive data entry, just fast and accurate
                  e-filing.{' '}
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Bulk upload?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whocan" sx={{ color: '#0202ff' }}>
                          Who can use bulk upload?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          What file formats will be accepted?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#steps" sx={{ color: '#0202ff' }}>
                          Steps to Bulk Upload Form 941 with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#benefits" sx={{ color: '#0202ff' }}>
                          Benefits of Bulk Upload with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is Bulk upload?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Bulk upload is a feature offered by TaxZerone which helps you
                  submit multiple Form 941 returns at once using a single excel
                  file. Instead of entering each employer’s information, you can
                  fill out an excel template with all the required data and
                  upload it in one go.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { md: 17, sm: 17, xs: 16 },
                          fontWeight: 600,
                        }}
                      >
                        Tired of entering Form 941 data one by one?
                      </Typography>
                      <Typography variant="body1">
                        Use our Bulk Upload feature and file your return in
                        minutes.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941bulk`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whocan"
                >
                  Who can use bulk upload?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Bulk upload can be used by{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    tax professionals, payroll service providers, and businesses
                  </span>{' '}
                  that need to{' '}
                  <span style={{ fontWeight: 600 }}>
                    <Link
                      href="/employment-tax/e-file-irs-form-941/"
                      sx={{ color: '#0202ff' }}
                    >
                      file Form 941
                    </Link>{' '}
                    for multiple clients or entities
                  </span>
                  . If you are managing filings for many employers, bulk upload
                  helps for faster processing, saving time, and reducing errors.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="file"
                >
                  What file formats will be accepted?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  TaxZerone accepts{' '}
                  <span style={{ fontWeight: 600 }}> Excel (.xlsx)</span> format
                  only. We already provided a preformatted
                  <br />
                  <Link
                    sx={{ color: '#0202ff', cursor: 'pointer' }}
                    onClick={() => {
                      setIsBulkUploadTemplate(true);
                    }}
                  >
                    Excel template
                  </Link>{' '}
                  for Form 941 bulk uploads. You fill in the excel sheet, upload
                  it, and you are ready to go.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="steps"
                >
                  Steps to Bulk Upload Form 941 with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Follow these steps to bulk upload your Form 941 with
                  TaxZerone.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 1:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941bulk`}
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Sign In
                    </Link>{' '}
                    to TaxZerone account using your credentials and move to the
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      “Employment Tax Dashboard”
                    </span>
                    .{' '}
                  </li>
                  <li>
                    {' '}
                    Then click on the “
                    <span style={{ fontWeight: 600 }}>Dashboard</span>” button
                    to begin your Employment Tax filing.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-bulk-dashboard-img.webp" />
                  <source srcSet="/assets/images/941-bulk-dashboard-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-bulk-dashboard-img.png"
                    loading="lazy"
                    alt="TaxZerone Home Application Dashboard "
                    width={769}
                    height={609}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 2:
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Select the “
                  <span style={{ fontWeight: 600 }}>Start New Return</span>”
                  button
                </Typography>
                <picture>
                  <source srcSet="/assets/images/941-bulk-started-img.webp" />
                  <source srcSet="/assets/images/941-bulk-started-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-bulk-started-img.png"
                    loading="lazy"
                    alt="On Employment Tax Dashboard, click on “Start New Return” to begin your filing. "
                    width={780}
                    height={327}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 3:
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Choose the “
                  <span style={{ fontWeight: 600 }}>Form 941- Bulk Upload</span>
                  ” option to file multiple employment tax forms together.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/941-bulk-img.webp" />
                  <source srcSet="/assets/images/941-bulk-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-bulk-img.png"
                    loading="lazy"
                    alt="Select Form 941 Bulk upload to easily file multiple 941 returns at once."
                    width={774}
                    height={507}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Step 4:
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  1.Upload Template
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Download the{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      bulk upload template (Excel format),
                    </span>{' '}
                    fill in the Form 941 details, and upload it.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-bulk-download-img.webp" />
                  <source srcSet="/assets/images/941-bulk-download-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-bulk-download-img.png"
                    loading="lazy"
                    alt="TaxZerone: Download Form 941 Bulk Upload template "
                    width={760}
                    height={281}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the employer information, tax details, Schedule B
                    (Month 1, Month 2, Month 3) accurately for each EIN/business
                    in the template and upload it.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-bulk-template-img.webp" />
                  <source srcSet="/assets/images/941-bulk-template-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-bulk-template-img.png"
                    loading="lazy"
                    alt="Form 941 Bulk Upload: Enter the employer information "
                    width={779}
                    height={310}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The grey colored fields in the template are automatically
                    calculated, so you can leave them blank.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-grey-color-img.webp" />
                  <source srcSet="/assets/images/941-grey-color-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-grey-color-img.png"
                    loading="lazy"
                    alt="Form 941 Bulk Upload: The grey colored fields are automatically calculated "
                    width={779}
                    height={54}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
                  (or){' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Upload your Excel file that includes all the necessary Form
                    941 details for each employer.
                  </li>
                  <li>
                    Make sure the information is completed and formatted
                    correctly to ensure a smooth and accurate bulk filing
                    process.{' '}
                  </li>
                  <li>
                    Click on  €˜<span style={{ fontWeight: 600 }}>Upload</span>’
                    to move on to the next step.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-bulk-upload-img.webp" />
                  <source srcSet="/assets/images/941-bulk-upload-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-bulk-upload-img.png"
                    loading="lazy"
                    alt="Form 941 Bulk Upload: Upload your Excel file"
                    width={780}
                    height={742}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  2. Preview Data
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Preview your uploaded data and make sure that all the
                    information has been accurately mapped to the correct fields
                    on Form 941.
                  </li>
                  <li>
                    If you notice any errors or mismatches, make the necessary
                    corrections before proceeding.
                  </li>
                  <li>
                    Then, click on{' '}
                    <span style={{ fontWeight: 600 }}>“Process File”</span> to
                    process the data.{' '}
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-process-file-img.webp" />
                  <source srcSet="/assets/images/941-process-file-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-process-file-img.png"
                    loading="lazy"
                    alt="Form 941 Bulk Upload: Preview your uploaded data matches the information displayed in the table."
                    width={772}
                    height={666}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  3. Upload summary
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the status of your Form 941 submission.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If any errors are detected, go to the{' '}
                    <span style={{ fontWeight: 600 }}> Error Records</span> tab
                    to view detailed information about the issues. From there,
                    you can make corrections in your file and re-upload the
                    updated version to continue with your submission.
                  </li>
                  <li>
                    If there are no corrections, click on the{' '}
                    <span style={{ fontWeight: 600 }}>“Go to Review”</span>{' '}
                    button.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-error-record-img.webp" />
                  <source srcSet="/assets/images/941-error-record-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-error-record-img.png"
                    loading="lazy"
                    alt="Check the status of your Form 941 bulk submission on upload summary. "
                    width={781}
                    height={542}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Step 5:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If the Action column shows{' '}
                    <span style={{ fontWeight: 600 }}>“Add Signature”</span>,
                    Click the button to attach an{' '}
                    <span style={{ fontWeight: 600 }}>
                      IRS-authorized electronic signature
                    </span>{' '}
                    (
                    <Link
                      href="/employment-tax/e-file-irs-form-94x-pin-request/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      94x Online Signature PIN
                    </Link>{' '}
                    or{' '}
                    <Link
                      href="/resources/employment-tax/form-941-signature-options/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 8453-EMP
                    </Link>
                    ) to the return.
                  </li>
                  <li style={{ listStyleType: 'none', textAlign: 'center' }}>
                    or
                  </li>
                  <li>
                    If the Action column shows only{' '}
                    <span style={{ fontWeight: 600 }}>“Audit”</span>, Click on
                    the button.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/941-audit-img.webp" />
                  <source srcSet="/assets/images/941-audit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-audit-img.png"
                    loading="lazy"
                    alt="If the Action column shows “Add Signature”, attach an IRS-authorized e-signature to the return or else click on “Audit”. "
                    width={781}
                    height={244}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Step 6:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Now all your returns are organized into two sections.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  1. Ready to E-File
                </Typography>

                <Typography variant="body1" id="deadline" sx={{ mb: 3 }}>
                  This list contains all the returns that are complete and ready
                  for submission. You can select multiple returns from this
                  section and e-file them in bulk.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/941-ready-file-img.webp" />
                  <source srcSet="/assets/images/941-ready-file-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-ready-file-img.png"
                    loading="lazy"
                    alt="Form 941 Bulk Upload: Ready to E-File"
                    width={780}
                    height={255}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  2. Not Ready Returns
                </Typography>

                <Typography variant="body1" id="deadline" sx={{ mb: 3 }}>
                  These are returns that still require validation. Click on
                  <span style={{ fontWeight: 600 }}>“Audit”</span> and the
                  return will move to the{' '}
                  <span style={{ fontWeight: 600 }}>Ready to E-File</span>{' '}
                  section.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/941-not-ready-return-img.webp" />
                  <source srcSet="/assets/images/941-not-ready-return-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-not-ready-return-img.png"
                    loading="lazy"
                    alt=" Form 941 Bulk Upload: Not Ready Returns"
                    width={780}
                    height={210}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Step 7:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter your card details and click{' '}
                  <span style={{ fontWeight: 600 }}> “Pay & e-File”</span> to
                  complete your payment and securely submit your return to the
                  IRS.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/941-card-deatisl-img.webp" />
                  <source srcSet="/assets/images/941-card-deatisl-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/941-card-deatisl-img.png"
                    loading="lazy"
                    alt="Form 941 Bulk Upload: Card details"
                    width={780}
                    height={364}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 3,
                    }}
                  />
                </picture>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="benefits"
                >
                  Benefits of Bulk Upload with TaxZerone
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Save Time and Effort:
                    </span>{' '}
                    Upload and file multiple returns at once, no need to enter
                    data manually for each form.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Easy Excel Integration:
                    </span>{' '}
                    Simply upload a pre-filled Excel sheet and let our system
                    map your data to the correct fields automatically.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Error Detection Made Easy:
                    </span>{' '}
                    Instantly identify and fix errors with detailed feedback
                    through the{' '}
                    <span style={{ fontWeight: 600 }}>
                      &quot;Error Records&quot;
                    </span>{' '}
                    tab before submission.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Faster E-Filing in Bulk:
                    </span>{' '}
                    Select all validated returns and e-file them in just a few
                    clicks—no repetitive steps.
                  </li>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to E-File with TaxZerone?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              <Link
                href="/employment-tax/e-file-irs-form-941/"
                sx={{ color: '#0202ff' }}
              >
                {' '}
                E-file all your Form 941
              </Link>{' '}
              returns in one simple upload —fast, <br />
              easy, and efficient with TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=941bulk`}
              >
                E-File Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form941BulkUpload;
